import {Component, computed, inject, input} from '@angular/core';
import {FormControl} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {Dropdown} from '../../../../components/dropdown/dropdown';
import {MultiDropdown} from '../../../../components/multi-dropdown/multi-dropdown';
import {ProductService, ProductType} from '../../../../../api';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {QueryKey} from '../../../../constants/query-key';

@Component({
  selector: 'elcia-product-dropdown',
  imports: [Dropdown, MultiDropdown, TranslatePipe],
  templateUrl: './product-dropdown.html',
  styleUrl: './product-dropdown.scss',
})
export class ProductDropdown {
  private readonly productService = inject(ProductService);

  isMulti = input.required<boolean>();
  control = input.required<FormControl>();
  // Optional: restrict the list to one or several product types (e.g. FEATURE for extra features,
  // or [LICENCE, MODULE] for the main customer-product dropdown). Filtering is done server-side.
  productType = input<ProductType | ProductType[] | null>(null);
  // Optional: ids of products to grey out (disabled option), e.g. features already included in the selected licence.
  disabledProductIds = input<ReadonlySet<number>>(new Set());

  // Normalized to an array so it can be used both as the cache key and the server-side type filter.
  private readonly types = computed(() => {
    const type = this.productType();
    if (type == null) return null;
    return Array.isArray(type) ? type : [type];
  });

  private readonly productsQuery = injectQuery(() => ({
    queryKey: [QueryKey.PRODUCT_SELECT, this.types()],
    queryFn: () => lastValueFrom(this.productService.getProductSelect(null, this.types())),
  }));

  protected readonly products = computed(() => {
    const all = this.productsQuery.data()?.products ?? [];
    const disabled = this.disabledProductIds();
    return all.map(product => ({...product, disabled: disabled.has(product.id)}));
  });
}
